'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Stack,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Fade,
  useTheme,
  useMediaQuery,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  Fab,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  LocalShipping as ShippingIcon,
  Calculate as CalculateIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Scale as ScaleIcon,
  AttachMoney as AttachMoneyIcon,
  LocationOn as LocationOnIcon,
  Inventory as InventoryIcon,
  AccountBalanceWallet,
} from '@mui/icons-material';

import { LayoutContainer } from '@/kit';
import { buildApiConfig } from '@/services/api/apiClient';
import { endPointEnums } from '@/services/api/endpoints';
import useApiCall from '@/services/api/useApiCall';
import { setBookingDetails } from '@/app/redux/slices/bookingSlice';

import BulkBookingTable from './components/BulkBookingTable';
import BulkSummary from './components/BulkSummary';
import BulkOrderConfirmDialog from './components/BulkOrderConfirmDialog';
import PrimaryPickupSelector from './components/PrimaryPickupSelector';
import RecentTemplates from './components/RecentTemplates';
import ContractAlertCard from './components/ContractAlertCard';
import OffersCard from './components/OffersCard';
import { useTranslation } from '@/kit/useTranslation';

// Theme colors (matching business dashboard)
const BUSINESS_COLORS = {
  primary: '#1a1a1a',
  accent: '#f59e0b',
  gradient: 'linear-gradient(135deg, #1a1a1a 0%, #333333 100%)',
  success: '#10b981',
  error: '#ef4444',
};

export default function BulkBookingPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnToBulkOrderId = searchParams?.get('bulkOrderId');
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { makeApiCall, loading } = useApiCall();

  const [walletBalance, setWalletBalance] = useState(0);
  const [pastBookingsCount, setPastBookingsCount] = useState(0);
  const user = useSelector((state) => state.auth?.user);
  const userId = user?.userId || user?.id;

  // Fetch wallet balance and stats
  const fetchWalletAndStats = useCallback(async () => {
    if (!userId) return;

    try {
      // Fetch wallet balance from wallet API - requires userId param
      // Pass userId as params (second argument) — do NOT use methodOverride
      const walletConfig = buildApiConfig(endPointEnums.WALLET_BALANCE, {
        userId,
      });
      const walletRes = await makeApiCall(walletConfig);
      if (walletRes?.success && walletRes?.data) {
        const balance = walletRes.data.balance?.available || walletRes.data.walletBalance || 0;
        setWalletBalance(Number(balance));
      }

      // Fetch business stats
      const statsConfig = buildApiConfig(endPointEnums.GET_BUSINESS_STATS);
      const statsRes = await makeApiCall(statsConfig);
      if (statsRes?.success && statsRes?.data) {
        setPastBookingsCount(statsRes.data.pastBookings || 0);
      }
    } catch (err) {
      console.error('[BulkBooking] fetchWalletAndStats failed', err);
    }
  }, [makeApiCall, userId]);

  const isLoggedIn = useSelector((state) => state.auth?.isLoggedIn);
  const isHydrated = useSelector((state) => state.auth?.isHydrated);
  const businessSetupCompleted = useSelector((state) => state.auth?.businessSetupCompleted);
  // Auth state — treat users who completed business setup as business users
  // regardless of whether their userType was changed in the backend yet.
  const isBusinessUser =
    user?.userType?.toUpperCase() === 'BUSINESS' ||
    user?.user_type === 'BUSINESS' ||
    businessSetupCompleted === true;

  // Fetch wallet and stats on auth/user change
  useEffect(() => {
    if (isLoggedIn && isBusinessUser && userId) {
      fetchWalletAndStats();
    }
  }, [isLoggedIn, isBusinessUser, userId, fetchWalletAndStats]);

  // Bulk order state
  const [bulkOrder, setBulkOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [primaryPickup, setPrimaryPickup] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Mobile pickup dialog
  const [pickupDialogOpen, setPickupDialogOpen] = useState(false);

  // Order confirmation dialog (shown before navigating to payment)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Redirect if not logged in or not business user — wait for hydration first
  useEffect(() => {
    if (!isHydrated) return;
    if (!isLoggedIn) {
      router.push('/login?redirect=/bulk-booking');
      return;
    }
    if (isLoggedIn && !isBusinessUser) {
      router.push('/business-setup?redirect=/bulk-booking');
    }
  }, [isHydrated, isLoggedIn, isBusinessUser, router]);

  // Apply a shipment template to the current bulk order
  const applyTemplate = useCallback(
    async (t) => {
      if (!t) return;
      // Set primary pickup from template origin
      if (t.origin) {
        if (typeof t.origin === 'string') {
          setPrimaryPickup({ address: t.origin });
        } else {
          setPrimaryPickup({
            address: t.origin.label || t.origin.address || t.origin.formatted_address,
            lat: t.origin.latitude || t.origin.lat,
            lng: t.origin.longitude || t.origin.lng,
          });
        }
      }

      // Build delivery item
      const delivery = {};
      if (t.destination) {
        if (typeof t.destination === 'string') {
          delivery.deliveryAddress = t.destination;
        } else {
          delivery.deliveryAddress =
            t.destination.label || t.destination.address || t.destination.formatted_address || '';
          delivery.deliveryLat = t.destination.latitude || t.destination.lat;
          delivery.deliveryLng = t.destination.longitude || t.destination.lng;
        }
      }

      delivery.recipientName = t.receiverName || '';
      delivery.recipientPhone = t.receiverPhone || '';
      delivery.weightKg = t.parcelWeight || 1;
      delivery.volumeCbm =
        t.volume && t.volume.length && t.volume.breadth && t.volume.height
          ? Number(t.volume.length) * Number(t.volume.breadth) * Number(t.volume.height)
          : 0;
      delivery.category =
        Array.isArray(t.parcelType) && t.parcelType.length > 0 ? t.parcelType[0] : 'GENERAL';

      const newItem = {
        itemId: `local_${Date.now()}`,
        ...delivery,
      };

      setItems((prev) => [...prev, newItem]);

      // Mark template as used on server
      try {
        const config = buildApiConfig(endPointEnums.useShipmentTemplate, {
          templateId: t.id,
        });
        await makeApiCall(config);
      } catch (err) {
        console.error('[BulkBooking] Failed to mark template used', err);
      }
    },
    [makeApiCall]
  );

  // Create or fetch draft bulk order on mount
  useEffect(() => {
    const initBulkOrder = async () => {
      if (!isLoggedIn || !isBusinessUser) return;

      try {
        // If returning from payment page with a specific order, load it directly
        if (returnToBulkOrderId) {
          const detailConfig = buildApiConfig(endPointEnums.GET_BULK_ORDER_BY_ID, {
            bulkOrderId: returnToBulkOrderId,
          });
          const detailResponse = await makeApiCall(detailConfig);

          if (detailResponse?.success && detailResponse.data) {
            let orderData = detailResponse.data;

            // If order is PENDING (submitted but unpaid), revert to DRAFT so user can edit
            if (orderData.status === 'PENDING') {
              try {
                const revertConfig = buildApiConfig(endPointEnums.REVERT_BULK_ORDER_TO_DRAFT, {
                  bulkOrderId: returnToBulkOrderId,
                });
                const revertRes = await makeApiCall(revertConfig);
                if (revertRes?.success && revertRes.data) {
                  orderData = revertRes.data;
                }
              } catch (revertErr) {
                console.warn('[BulkBooking] Failed to revert order to draft:', revertErr);
              }
            }

            setBulkOrder(orderData);
            setItems(orderData.items || []);
            if (orderData.primaryPickupAddress) {
              setPrimaryPickup({
                address: orderData.primaryPickupAddress,
                lat: orderData.primaryPickupLat,
                lng: orderData.primaryPickupLng,
              });
            }
            return;
          }
        }

        // First check for existing draft
        const listConfig = buildApiConfig(endPointEnums.GET_BULK_ORDERS, {
          status: 'DRAFT',
          limit: 1,
        });
        const listResponse = await makeApiCall(listConfig);

        if (listResponse?.success && listResponse.data?.length > 0) {
          // Use existing draft
          const existingOrder = listResponse.data[0];
          const detailConfig = buildApiConfig(endPointEnums.GET_BULK_ORDER_BY_ID, {
            bulkOrderId: existingOrder.bulkOrderId,
          });
          const detailResponse = await makeApiCall(detailConfig);

          if (detailResponse?.success) {
            setBulkOrder(detailResponse.data);
            setItems(detailResponse.data.items || []);
            if (detailResponse.data.primaryPickupAddress) {
              setPrimaryPickup({
                address: detailResponse.data.primaryPickupAddress,
                lat: detailResponse.data.primaryPickupLat,
                lng: detailResponse.data.primaryPickupLng,
              });
            }
          }
        } else {
          // Create new draft
          const createConfig = buildApiConfig(endPointEnums.CREATE_BULK_ORDER, {});
          const createResponse = await makeApiCall(createConfig);

          if (createResponse?.success) {
            setBulkOrder(createResponse.data);
            setItems([]);
          }
        }
      } catch (err) {
        console.error('[BulkBooking] Init error:', err);
        setError(t('bulkBooking.initError'));
      }
    };

    initBulkOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, isBusinessUser]);

  // Update primary pickup address
  const handlePrimaryPickupChange = useCallback(
    async (pickupData) => {
      // Always update local state immediately — even if bulk order not yet loaded
      setPrimaryPickup(pickupData);

      // Skip API call if bulkOrder isn't initialized yet — will be re-synced on calculate
      if (!bulkOrder || !pickupData) return;

      try {
        const config = buildApiConfig(endPointEnums.UPDATE_BULK_ORDER, {
          bulkOrderId: bulkOrder.bulkOrderId,
          primaryPickupAddress: pickupData.address,
          primaryPickupLat: pickupData.lat,
          primaryPickupLng: pickupData.lng,
        });
        const result = await makeApiCall(config);
        if (result?.success === false) {
          console.warn('[BulkBooking] Pickup save returned failure:', result.message);
        }
      } catch (err) {
        console.error('[BulkBooking] Failed to update primary pickup:', err);
        setSnackbar({
          open: true,
          message: 'Pickup address saved locally. Will be synced on calculate.',
          severity: 'warning',
        });
      }
    },
    [bulkOrder, makeApiCall]
  );

  // Add new item
  const handleAddItem = useCallback(async () => {
    if (!bulkOrder) return;

    try {
      const config = buildApiConfig(endPointEnums.ADD_BULK_ORDER_ITEM, {
        bulkOrderId: bulkOrder.bulkOrderId,
        usePrimaryPickup: true,
        weightKg: 1,
        category: 'GENERAL',
        deliveryAddress: {},
      });
      const response = await makeApiCall(config);

      if (response?.success) {
        setItems((prev) => [...prev, response.data]);
        setSnackbar({
          open: true,
          message: t('bulkBooking.rowAdded'),
          severity: 'success',
        });
      }
    } catch (err) {
      console.error('[BulkBooking] Add item error:', err);
      setError(t('bulkBooking.addItemError'));
    }
  }, [bulkOrder, makeApiCall]);

  // Calculate pricing
  const handleCalculatePricing = useCallback(async () => {
    if (!bulkOrder) return;

    // Frontend guard: validate that a pickup with coordinates is selected
    if (!primaryPickup?.lat || !primaryPickup?.lng) {
      setError(
        'Please select a primary pickup address using the search box above before calculating pricing.'
      );
      return;
    }

    setIsCalculating(true);
    setError('');

    try {
      // ALWAYS re-sync pickup to backend before calculating.
      // This fixes: (1) mobile dialog only setting local state, (2) race conditions.
      const pickupSyncConfig = buildApiConfig(endPointEnums.UPDATE_BULK_ORDER, {
        bulkOrderId: bulkOrder.bulkOrderId,
        primaryPickupAddress: primaryPickup.address,
        primaryPickupLat: primaryPickup.lat,
        primaryPickupLng: primaryPickup.lng,
      });
      const syncResult = await makeApiCall(pickupSyncConfig);
      if (syncResult?.success === false) {
        setError(
          'Failed to sync pickup address with server. Please re-select your pickup and try again.'
        );
        setIsCalculating(false);
        return;
      }

      const config = buildApiConfig(endPointEnums.CALCULATE_BULK_PRICING, {
        bulkOrderId: bulkOrder.bulkOrderId,
      });
      const response = await makeApiCall(config);

      if (response?.success) {
        setBulkOrder(response.data);
        setItems(response.data.items || []);
        setSnackbar({
          open: true,
          message: t('bulkBooking.pricingCalculated'),
          severity: 'success',
        });
      } else {
        const errMsg =
          response?.message || response?.error || t('bulkBooking.calculatePricingError');
        setError(errMsg);
      }
    } catch (err) {
      console.error('[BulkBooking] Calculate pricing error:', err);
      setError(t('bulkBooking.calculatePricingError'));
    } finally {
      setIsCalculating(false);
    }
  }, [bulkOrder, primaryPickup, makeApiCall, t]);

  // Update item
  const handleUpdateItem = useCallback(
    async (itemId, updates) => {
      if (!bulkOrder) return;

      try {
        const config = buildApiConfig(endPointEnums.UPDATE_BULK_ORDER_ITEM, {
          bulkOrderId: bulkOrder.bulkOrderId,
          itemId,
          ...updates,
        });
        const response = await makeApiCall(config);

        if (response?.success) {
          setItems((prev) => prev.map((item) => (item.itemId === itemId ? response.data : item)));
          // Auto-recalculate when a delivery address is confirmed and pricing has run before
          if (updates.deliveryAddress && Number(bulkOrder.totalAmount) > 0) {
            setTimeout(handleCalculatePricing, 1200);
          }
        }
      } catch (err) {
        console.error('[BulkBooking] Update item error:', err);
      }
    },
    [bulkOrder, makeApiCall, handleCalculatePricing]
  );

  // Delete item
  const handleDeleteItem = useCallback(
    async (itemId) => {
      if (!bulkOrder) return;

      try {
        const config = buildApiConfig(endPointEnums.DELETE_BULK_ORDER_ITEM, {
          bulkOrderId: bulkOrder.bulkOrderId,
          itemId,
        });
        const response = await makeApiCall(config);

        if (response?.success) {
          const remaining = items.filter((item) => item.itemId !== itemId);
          setItems(remaining);
          setSnackbar({
            open: true,
            message: t('bulkBooking.rowDeleted'),
            severity: 'info',
          });
          // Auto-recalculate if pricing was previously applied and items remain
          if (remaining.length > 0 && Number(bulkOrder.totalAmount) > 0) {
            handleCalculatePricing();
          }
        }
      } catch (err) {
        console.error('[BulkBooking] Delete item error:', err);
        setError(t('bulkBooking.deleteItemError'));
      }
    },
    [bulkOrder, makeApiCall, items, handleCalculatePricing, t]
  );

  // Duplicate item
  const handleDuplicateItem = useCallback(
    async (itemId) => {
      if (!bulkOrder) return;

      try {
        const config = buildApiConfig(endPointEnums.DUPLICATE_BULK_ORDER_ITEM, {
          bulkOrderId: bulkOrder.bulkOrderId,
          itemId,
        });
        const response = await makeApiCall(config);

        if (response?.success) {
          setItems((prev) => [...prev, response.data]);
          setSnackbar({
            open: true,
            message: t('bulkBooking.rowDuplicated'),
            severity: 'success',
          });
        }
      } catch (err) {
        console.error('[BulkBooking] Duplicate item error:', err);
        setError(t('bulkBooking.duplicateItemError'));
      }
    },
    [bulkOrder, makeApiCall]
  );

  // Show confirmation dialog — validates then opens review modal
  const handleSubmitOrder = useCallback(async () => {
    if (!bulkOrder) return;

    // Validation
    if (items.length === 0) {
      setError(t('bulkBooking.addDeliveryError'));
      return;
    }

    if (!primaryPickup) {
      setError(t('bulkBooking.selectPickupError'));
      return;
    }

    // Check all items have delivery addresses (handle both old and new formats)
    const incompleteItems = items.filter((item) => {
      if (!item.deliveryAddress) return true;

      // New format: check for latitude in object
      if (typeof item.deliveryAddress === 'object' && item.deliveryAddress.latitude) {
        return false;
      }

      // Old format: check for separate lat/lng fields
      if (item.deliveryLat && item.deliveryLng) {
        return false;
      }

      return true;
    });
    if (incompleteItems.length > 0) {
      setError(t('bulkBooking.missingAddressError', { count: incompleteItems.length }));
      return;
    }

    // Guard: check pricing was calculated
    const totalAmount = Number(bulkOrder.totalAmount || 0);
    if (totalAmount <= 0) {
      setError(t('bulkBooking.zeroTotalError'));
      return;
    }

    // Open confirmation dialog
    setConfirmDialogOpen(true);
  }, [bulkOrder, items, primaryPickup, t]);

  // Actually submit order and navigate to payment (called from confirm dialog)
  const handleConfirmAndPay = useCallback(async () => {
    if (!bulkOrder) return;

    setIsSubmitting(true);
    setError('');

    try {
      const config = buildApiConfig(endPointEnums.SUBMIT_BULK_ORDER, {
        bulkOrderId: bulkOrder.bulkOrderId,
      });
      const response = await makeApiCall(config);

      if (response?.success) {
        // Update local state with server's canonical order (ensures totals are fresh)
        if (response.data) {
          setBulkOrder(response.data);
          setItems(response.data.items || []);
        }

        setSnackbar({
          open: true,
          message: t('bulkBooking.orderSubmitted'),
          severity: 'success',
        });

        // Guard: do not continue to payment if amount is zero
        const totalAmount = response?.data?.totalAmount || response?.data?.total_amount || 0;
        if (Number(totalAmount) <= 0) {
          setError(t('bulkBooking.zeroTotalError'));
          setIsSubmitting(false);
          setConfirmDialogOpen(false);
          return;
        }

        // Populate Redux booking store for payment page
        dispatch(
          setBookingDetails({
            bulkOrderId: bulkOrder.bulkOrderId,
            estimatedPrice: totalAmount,
            totalAmount: totalAmount,
            type: 'BULK_ORDER',
            itemCount: items.length,
            primaryPickupAddress: primaryPickup?.address,
            ...response.data,
          })
        );

        // Navigate to payment with amount (helps payment flow avoid extra fetches)
        // Do NOT reset isSubmitting here - let navigation complete with loader showing
        router.push(
          `/payment?bulkOrderId=${bulkOrder.bulkOrderId}&amount=${encodeURIComponent(totalAmount)}`
        );
      }
    } catch (err) {
      console.error('[BulkBooking] Submit error:', err);
      setError(err.message || t('bulkBooking.submitError'));
      setIsSubmitting(false); // Only reset on error
      setConfirmDialogOpen(false);
    }
  }, [bulkOrder, items, primaryPickup, makeApiCall, router, dispatch]);

  // Cancel order
  const handleCancelOrder = useCallback(async () => {
    if (!bulkOrder) return;

    if (!confirm(t('bulkBooking.cancelConfirm'))) return;

    try {
      const config = buildApiConfig(endPointEnums.DELETE_BULK_ORDER, {
        bulkOrderId: bulkOrder.bulkOrderId,
      });
      await makeApiCall(config);

      setBulkOrder(null);
      setItems([]);
      setPrimaryPickup(null);
      router.push('/business-dashboard');
    } catch (err) {
      console.error('[BulkBooking] Cancel error:', err);
      setError(t('bulkBooking.cancelError'));
    }
  }, [bulkOrder, makeApiCall, router]);

  // Loading state
  if (loading && !bulkOrder) {
    return (
      <LayoutContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress sx={{ color: BUSINESS_COLORS.accent }} />
        </Box>
      </LayoutContainer>
    );
  }

  // Not authorized
  if (!isBusinessUser && isLoggedIn) {
    return (
      <LayoutContainer>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert severity="warning">{t('bulkBooking.businessOnlyAlert')}</Alert>
          <Button
            variant="contained"
            onClick={() => router.push('/business-setup')}
            sx={{ mt: 2, bgcolor: BUSINESS_COLORS.accent }}
          >
            {t('bulkBooking.completeBusinessSetup')}
          </Button>
        </Container>
      </LayoutContainer>
    );
  }

  return (
    <LayoutContainer>
      <Box sx={{ pb: { xs: 26, md: 4 } }}>
        {/* Hero Header with Gradient (matching profile style) */}
        <Box
          sx={{
            background:
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #222426 0%, #2f3332 50%, #222426 100%)'
                : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            py: { xs: 2, md: 3 },
            px: { xs: 2, md: 3 },
            mb: 2,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 0.15 },
              '50%': { opacity: 0.25 },
            },
          }}
        >
          {/* Animated background circles */}
          <Box
            sx={{
              position: 'absolute',
              top: '-50%',
              right: '-10%',
              width: { xs: '250px', md: '400px' },
              height: { xs: '250px', md: '400px' },
              borderRadius: '50%',
              background: alpha('#fff', 0.08),
              animation: 'pulse 4s ease-in-out infinite',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '-30%',
              left: '-5%',
              width: { xs: '200px', md: '300px' },
              height: { xs: '200px', md: '300px' },
              borderRadius: '50%',
              background: alpha('#fff', 0.06),
              animation: 'pulse 5s ease-in-out infinite 1s',
            }}
          />

          <Box sx={{ maxWidth: 1200, mx: 'auto', position: 'relative', zIndex: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: '1.5rem', md: '1.75rem' },
                    fontWeight: 800,
                    color: '#fff',
                    textShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  }}
                >
                  {t('bulkBooking.pageTitle')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '0.75rem', md: '0.85rem' },
                    color: alpha('#fff', 0.9),
                    mt: { xs: 0.25, md: 0.5 },
                  }}
                >
                  {bulkOrder?.bulkOrderReference || t('bulkBooking.newOrder')} • {items.length}{' '}
                  {items.length !== 1
                    ? t('bulkBooking.itemCount_plural', { count: items.length })
                    : t('bulkBooking.itemCount', { count: items.length })}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelOrder}
                  startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    textTransform: 'none',
                    borderColor: alpha('#fff', 0.3),
                    color: '#fff',
                    bgcolor: alpha('#000', 0.1),
                    '&:hover': {
                      borderColor: '#fff',
                      bgcolor: alpha('#000', 0.2),
                    },
                  }}
                >
                  {t('bulkBooking.cancel')}
                </Button>

                {/* Mobile pickup quick action */}
                <IconButton
                  size="small"
                  onClick={() => setPickupDialogOpen(true)}
                  sx={{
                    display: { xs: 'inline-flex', md: 'none' },
                    color: '#fff',
                    ml: 1,
                    bgcolor: alpha('#000', 0.06),
                    '&:hover': { bgcolor: alpha('#000', 0.12) },
                  }}
                >
                  <LocationOnIcon />
                </IconButton>
              </Stack>
            </Stack>

            {/* Mobile sticky action bar (only xs) — sits ABOVE the BottomNav */}
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                position: 'fixed',
                bottom: 'calc(7vh + 12px)',
                left: 16,
                right: 16,
                zIndex: 1400,
                p: 1.5,
                bgcolor: '#fff',
                borderRadius: 2,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 800 }}>
                  {items.length}{' '}
                  {items.length !== 1
                    ? t('bulkBooking.itemCount_plural', { count: items.length })
                    : t('bulkBooking.itemCount', { count: items.length })}{' '}
                  • ₹{Math.round(bulkOrder?.totalAmount || 0)}
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: '0.85rem' }}>
                  {items.reduce((s, it) => s + (parseFloat(it.weightKg) || 0), 0).toFixed(1)} kg
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddItem}
                  sx={{ textTransform: 'none' }}
                >
                  {t('bulkBooking.add')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSubmitOrder}
                  sx={{
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  }}
                >
                  {t('bulkBooking.proceed')}
                </Button>
              </Stack>
            </Box>

            {/* Mobile floating add button */}
            <Fab
              color="primary"
              aria-label="add"
              sx={{
                position: 'fixed',
                right: 18,
                bottom: 'calc(7vh + 100px)',
                zIndex: 1400,
                display: { xs: 'flex', md: 'none' },
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              }}
              onClick={handleAddItem}
            >
              <AddIcon />
            </Fab>
            <Stack
              direction={{ xs: 'row', md: 'row' }}
              spacing={1}
              sx={{
                mt: 2,
                display: { xs: 'grid', md: 'flex' },
                gridTemplateColumns: { xs: 'repeat(3, 1fr)', md: 'none' },
                columnGap: { xs: 0.5, md: 1.5 },
                gap: { xs: 0.5, md: 1.5 },
                px: { xs: 0.5, md: 0 },
                justifyItems: 'stretch',
                overflowX: 'visible',
              }}
            >
              <Paper
                sx={{
                  width: { xs: '100%', md: 'auto' },
                  p: { xs: 0.6, md: 1.25 },
                  minHeight: { xs: 64, md: 'auto' },
                  borderRadius: 2.25,
                  bgcolor: alpha('#fff', 0.14),
                  backdropFilter: 'blur(6px)',
                  border: '1px solid',
                  borderColor: alpha('#fff', 0.1),
                  transition: 'all 200ms',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      bgcolor: alpha('#fff', 0.22),
                      p: { xs: 0.5, md: 1 },
                      borderRadius: 1.25,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AccountBalanceWallet sx={{ fontSize: { xs: 16, md: 20 }, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.58rem', md: '0.5rem' },
                        color: alpha('#fff', 0.85),
                        fontWeight: 700,
                        letterSpacing: 0.5,
                      }}
                    >
                      {t('bulkBooking.walletBalance')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.95rem', md: '1.1rem' },
                        fontWeight: 800,
                        color: '#fff',
                      }}
                    >
                      ₹{walletBalance?.toFixed ? walletBalance.toFixed(0) : walletBalance || 0}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <Paper
                sx={{
                  width: { xs: '100%', md: 'auto' },
                  p: { xs: 0.6, md: 1.25 },
                  minHeight: { xs: 64, md: 'auto' },
                  borderRadius: 2.25,
                  bgcolor: alpha('#fff', 0.14),
                  backdropFilter: 'blur(6px)',
                  border: '1px solid',
                  borderColor: alpha('#fff', 0.1),
                  transition: 'all 200ms',
                  '&:hover': {
                    bgcolor: alpha('#fff', 0.18),
                    transform: { md: 'translateY(-2px)' },
                    boxShadow: { md: '0 8px 24px rgba(0,0,0,0.12)' },
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Box
                    sx={{
                      bgcolor: alpha('#fff', 0.22),
                      p: { xs: 0.5, md: 1 },
                      borderRadius: 1.25,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ShippingIcon sx={{ fontSize: { xs: 16, md: 20 }, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.58rem', md: '0.65rem' },
                        color: alpha('#fff', 0.85),
                        fontWeight: 700,
                        letterSpacing: 0.5,
                      }}
                    >
                      {t('bulkBooking.bookings')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.95rem', md: '1.1rem' },
                        fontWeight: 800,
                        color: '#fff',
                      }}
                    >
                      {pastBookingsCount || 0}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <Paper
                sx={{
                  width: { xs: '100%', md: 'auto' },
                  p: { xs: 0.6, md: 1.5 },
                  minHeight: { xs: 64, md: 'auto' },
                  borderRadius: 2.25,
                  bgcolor: alpha('#fff', 0.14),
                  backdropFilter: 'blur(6px)',
                  border: '1px solid',
                  borderColor: alpha('#fff', 0.1),
                  transition: 'all 200ms',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Box
                    sx={{
                      bgcolor: alpha('#fff', 0.22),
                      p: { xs: 0.5, md: 1 },
                      borderRadius: 1.25,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ScaleIcon sx={{ fontSize: { xs: 16, md: 20 }, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.58rem', md: '0.65rem' },
                        color: alpha('#fff', 0.85),
                        fontWeight: 700,
                        letterSpacing: 0.5,
                      }}
                    >
                      {t('bulkBooking.totalWeight')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.95rem', md: '1.1rem' },
                        fontWeight: 800,
                        color: '#fff',
                      }}
                    >
                      {items
                        .reduce((sum, item) => sum + (parseFloat(item.weightKg) || 0), 0)
                        .toFixed(1)}{' '}
                      kg
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* <Paper
                  sx={{
                    width: '100%',
                    p: { xs: 0.6, md: 1.25 },
                    minHeight: { xs: 64, md: 'auto' },
                    borderRadius: 2.25,
                    bgcolor: alpha('#fff', 0.14),
                    backdropFilter: 'blur(6px)',
                    border: '1px solid',
                    borderColor: alpha('#fff', 0.10),
                    transition: 'all 200ms',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Box
                      sx={{
                        bgcolor: alpha('#fff', 0.22),
                        p: { xs: 0.5, md: 1 },
                        borderRadius: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <InventoryIcon sx={{ fontSize: { xs: 16, md: 20 }, color: '#fff' }} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: { xs: '0.58rem', md: '0.65rem' },
                          color: alpha('#fff', 0.85),
                          fontWeight: 700,
                          letterSpacing: 0.5,
                        }}
                      >
                        ITEMS
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: '0.95rem', md: '1.1rem' },
                          fontWeight: 800,
                          color: '#fff',
                        }}
                      >
                        {items.length}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper> */}
            </Stack>

            {/* Mobile pickup summary - below stats */}
            <Paper
              elevation={0}
              sx={{
                display: { xs: 'flex', md: 'none' },
                mt: 1,
                p: 1,
                bgcolor: '#fff',
                color: BUSINESS_COLORS.primary,
                borderRadius: 2,
                alignItems: 'center',
                justifyContent: 'space-between',
                border: '1px solid #e6e6e6',
                boxShadow: '0 6px 20px rgba(15,23,42,0.04)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    p: 0.5,
                    borderRadius: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  }}
                >
                  <LocationOnIcon sx={{ color: '#fff', fontSize: 16 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#64748b',
                      fontWeight: 700,
                    }}
                  >
                    {t('bulkBooking.pickup')}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: BUSINESS_COLORS.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 220,
                      fontWeight: 700,
                    }}
                  >
                    {primaryPickup?.address || primaryPickup?.label || t('bulkBooking.noPickupSet')}
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small"
                variant="contained"
                onClick={() => setPickupDialogOpen(true)}
                sx={{
                  bgcolor: 'transparent',
                  color: BUSINESS_COLORS.primary,
                  border: '1px solid #e6e6e6',
                  '&:hover': { bgcolor: '#fff' },
                }}
              >
                {t('bulkBooking.edit')}
              </Button>
            </Paper>
          </Box>
        </Box>

        <Container maxWidth="xl">
          <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            {/* Error Alert */}
            {error && (
              <Fade in>
                <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
                  {error}
                </Alert>
              </Fade>
            )}

            {/* Primary Pickup Selector - Compact */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: 640,
                  display: { xs: 'none', md: 'block' },
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                    background: alpha(BUSINESS_COLORS.accent, 0.02),
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: BUSINESS_COLORS.primary,
                      mb: 1.25,
                      fontSize: '0.9rem',
                    }}
                  >
                    {t('bulkBooking.primaryPickupAddress')}
                  </Typography>
                  <PrimaryPickupSelector
                    value={primaryPickup}
                    onChange={handlePrimaryPickupChange}
                  />
                </Paper>

                {/* Mobile pickup dialog trigger */}
                <Dialog
                  open={pickupDialogOpen}
                  onClose={() => setPickupDialogOpen(false)}
                  fullWidth
                >
                  <DialogTitle>{t('bulkBooking.primaryPickup')}</DialogTitle>
                  <DialogContent dividers>
                    <PrimaryPickupSelector
                      value={primaryPickup}
                      onChange={(v) => {
                        // Call handlePrimaryPickupChange so the address is saved to the backend too
                        handlePrimaryPickupChange(v);
                        setPickupDialogOpen(false);
                      }}
                    />
                  </DialogContent>
                </Dialog>
              </Box>

              <Box sx={{ width: 360 }}>
                {/* <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                    mb: 1,
                  }}
                >
                  <RecentTemplates onApply={(t) => applyTemplate(t)} />
                </Paper> */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                    mb: 1,
                  }}
                >
                  <ContractAlertCard />
                </Paper>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  <OffersCard />
                </Paper>
              </Box>
            </Box>

            {/* Items Table */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
                mb: 3,
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: '#fafafa',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: BUSINESS_COLORS.primary }}
                >
                  {t('bulkBooking.deliveryItems')}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddItem}
                  sx={{
                    bgcolor: BUSINESS_COLORS.accent,
                    '&:hover': { bgcolor: '#d97706' },
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {t('bulkBooking.addItems')}
                </Button>
              </Box>

              <BulkBookingTable
                items={items}
                primaryPickup={primaryPickup}
                onUpdateItem={handleUpdateItem}
                onDeleteItem={handleDeleteItem}
                onDuplicateItem={handleDuplicateItem}
                isMobile={isMobile}
                bulkOrder={bulkOrder}
                showBulkBadge={false}
              />
            </Paper>

            {/* Summary & Actions */}
            <BulkSummary
              bulkOrder={bulkOrder}
              items={items}
              onCalculate={handleCalculatePricing}
              onSubmit={handleSubmitOrder}
              isCalculating={isCalculating}
              isSubmitting={isSubmitting}
            />
          </Box>
        </Container>
      </Box>

      {/* Order Confirmation Dialog */}
      <BulkOrderConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmAndPay}
        bulkOrder={bulkOrder}
        items={items}
        primaryPickup={primaryPickup}
        loading={isSubmitting}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </LayoutContainer>
  );
}
